<template>
  <div v-rect-loading="loading" class="template-field-container">
    <div class="template-field-editor-header">
      <div class="template-field-editor-title">{{ titleLabel }}</div>
    </div>
    <div class="container">
      <WrappedForm
        ref="form"
        class="template-field-info"
        label-width="220px"
        :model="fieldInfo"
        :formLines="formLines"
      ></WrappedForm>
    </div>
    <TableList
      v-if="hasOption"
      row-key="id"
      class="template-field-option-list"
      :createBtnLabelI18nKey="`${i18nPrefix}.label.addOption`"
      :listData="optionList"
      :tableColumns="tableColumns"
      :actionBtn="actionBtn"
      @create="handleCreateOption"
    >
      <template #draggable>
        <div class="drag-btn">
        <i class="iconfont iconMore draggable-handle"></i>
        </div>
      </template>
    </TableList>
    <div class="footer-group">
      <el-button class="btn cancel" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
      <el-button class="btn save" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
    </div>
    <DialogWrapper
      saveBtnLabelI18nKey="common.btn.confirm"
      closeOnCancel
      :title="optionDialog.title"
      :show.sync="optionDialog.show"
      @save="handleSaveOption"
    >
      <WrappedForm
        ref="optionForm"
        class="option-edit-form"
        label-width="220px"
        :model="optionData"
        :formLines="optionFormLines"
      >
      </WrappedForm>
    </DialogWrapper>
    <DialogWrapper
      saveBtnLabelI18nKey="common.btn.confirm"
      closeOnCancel
      :title="deleteDialog.title"
      :show.sync="deleteDialog.show"
      @save="handleDelete"
    >
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { OFFER_LETTER_TEMPLATE_FIELD_TYPE } from '@/constant/company'

export default {
  components: {
    WrappedForm,
    TableList,
    DialogWrapper
  },
  data () {
    return {
      OFFER_LETTER_TEMPLATE_FIELD_TYPE,
      i18nPrefix: 'admin.company.offerLetterInviteRecordManagement',
      loading: false,
      fieldInfo: {
        key: null,
        name_cn: null,
        name_en: null,
        type: null,
        value_type: OFFER_LETTER_TEMPLATE_FIELD_TYPE.SELECT
      },
      optionList: [],
      optionData: {},
      optionDialog: {
        show: false,
        title: null,
        editIndex: null
      },
      deleteDialog: {
        show: false,
        title: null,
        deleteIndex: null
      }
    }
  },
  computed: {
    fieldId () {
      return this.$route.params.fieldId
    },
    isEdit () {
      return this.$route.meta.isEdit
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}.title.${this.isEdit ? 'offerLetterTemplateFieldEdit' : 'offerLetterTemplateFieldAdd'}`)
    },
    optionValueTypeList () {
      return _.chain(OFFER_LETTER_TEMPLATE_FIELD_TYPE).map((val, key) => {
        if (key === OFFER_LETTER_TEMPLATE_FIELD_TYPE.SIGN) {
          return
        }
        return {
          label: this.$t(`${this.i18nPrefix}.OFFER_LETTER_TEMPLATE_FIELD_TYPE.${key}`),
          value: val
        }
      }).filter().value()
    },
    formLines () {
      const lines = {
        key: {
          label: this.$t(`${this.i18nPrefix}.label.templateFieldKey`),
          disabled: this.isEdit,
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }, { validator: this.validateKey, trigger: 'blur' }]
        },
        type: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.templateFieldType`),
          option: {
            data: [
              {
                label: this.$t(`${this.i18nPrefix}.label.templateFieldHR`),
                value: 'HR'
              },
              {
                label: this.$t(`${this.i18nPrefix}.label.templateFieldCandidate`),
                value: 'CANDIDATE'
              }
            ]
          },
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'change' }]
        },
        name_cn: {
          label: this.$t(`${this.i18nPrefix}.label.templateFieldCnName`),
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        name_en: {
          label: this.$t(`${this.i18nPrefix}.label.templateFieldEnName`),
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        value_type: {
          label: this.$t(`${this.i18nPrefix}.label.templateFieldValueType`),
          disabled: this.isEdit,
          elType: 'el-select',
          option: {
            data: this.optionValueTypeList
          },
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'change' }]
        }
      }
      return lines
    },
    actionBtn () {
      return {
        edit: {
          show: true,
          labelI18nKey: 'common.btn.edit',
          onclick: this.handleEditOption
        },
        delete: {
          show: true,
          labelI18nKey: 'common.btn.delete',
          onclick: this.handleDeleteOption
        }
      }
    },
    hasOptionFilterType () {
      return [OFFER_LETTER_TEMPLATE_FIELD_TYPE.SINGLE, OFFER_LETTER_TEMPLATE_FIELD_TYPE.SELECT]
    },
    optionFormLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const lines = {
        name_cn: {
          label: this.$t(`${this.i18nPrefix}.label.templateFieldCnName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        name_en: {
          label: this.$t(`${this.i18nPrefix}.label.templateFieldEnName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        }
      }
      return lines
    },
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.templateFieldCnName`),
          dataKey: 'name_cn'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.templateFieldEnName`),
          dataKey: 'name_en'
        },
        {
          label: this.$t('common.label.sortOperate'),
          isDragSort: true,
          slotName: 'draggable',
          width: 50
        }
      ]
      return columns
    },
    hasOption () {
      return this.hasOptionFilterType.includes(this.fieldInfo.value_type)
    },
    disabledSave () {
      const length = this.optionList?.length
      if (this.hasOption && (!length || length === 0)) {
        return true
      }
      return false
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('offerLetter', ['getOfferLetterTemplateFieldInfoById', 'createOfferLetterTemplateField', 'updateOfferLetterTemplateField']),
    initPage () {
      if (this.fieldId) {
        this.loadTemplateFieldInfo()
      }
    },
    async loadTemplateFieldInfo () {
      this.loading = true
      try {
        const resp = await this.getOfferLetterTemplateFieldInfoById(this.fieldId)
        if (resp) {
          this.fieldInfo = resp
          this.optionList = resp.option_list
        }
      } finally {
        this.loading = false
      }
    },
    validateKey (rule, value, callback) {
      if (value && !/^[A-Za-z]+$/.test(value)) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.keyIsEnglish`)))
      } else {
        callback()
      }
    },
    async handleSave () {
      if (!await this.$refs.form.submit()) {
        return
      }
      if (this.hasOption) {
        _.forEach(this.optionList, (option, index) => {
          option.order = index
        })
        this.fieldInfo.option_list = this.optionList
      }
      this.loading = true
      try {
        if (this.isEdit) {
          await this.updateOfferLetterTemplateField(this.fieldInfo)
        } else {
          await this.createOfferLetterTemplateField(this.fieldInfo)
        }
        this.handleCancel()
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      this.$router.push('/v-admin/offer-letter-management/field/list')
    },
    handleCreateOption () {
      this.optionDialog.show = true
      this.optionDialog.title = this.$t(`${this.i18nPrefix}.label.addOption`)
      this.optionDialog.editIndex = null
      this.optionData = {}
    },
    handleEditOption (row, index) {
      this.optionDialog.show = true
      this.optionDialog.title = this.$t(`${this.i18nPrefix}.label.editOption`)
      this.optionDialog.editIndex = index
      this.optionData = { ...row }
    },
    handleDeleteOption (row, index) {
      this.deleteDialog.show = true
      this.deleteDialog.title = this.$t(`${this.i18nPrefix}.message.deleteOption`, { cnName: row.name_cn, enName: row.name_en })
      this.deleteDialog.deleteIndex = index
    },
    async handleSaveOption () {
      const form = this.$refs.optionForm
      let result = false
      result = await form.submit()
      if (!result) {
        return
      }
      const editIndex = this.optionDialog.editIndex
      if (_.isNull(editIndex)) {
        this.optionList.push({ ...this.optionData })
      } else {
        this.optionList.splice(editIndex, 1, { ...this.optionData })
      }
      this.optionData = {}
      this.optionDialog.show = false
    },
    handleDelete () {
      this.optionList.splice(this.deleteDialog.deleteIndex, 1)
      this.deleteDialog.show = false
    }
  }
}
</script>

<style lang="scss" scoped>
.template-field-container {
  .template-field-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;
    .template-field-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }
  .container {
    width: 640px;
    margin: 30px auto;
  }
  .template-field-option-list {
    margin-bottom: 30px;

    .drag-btn {
      .draggable-handle {
        cursor: pointer;
      }
    }
  }
  .footer-group {
    margin-top: 30px;
    display: flex;
    justify-content: flex-end;
    .btn {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;
    }
    .save {
      margin-left: 24px;
      margin-right: 0;
      background: #0096cc;
      border-color: #0096cc;
      color: #fff;
    }
  }
}
</style>
